/* Vibranet × DigiFluss — pagina "Soluzioni digitali".
   Diagramma animato dell'hero, simulatore di sincronizzazione, comparse allo scroll,
   preselezione del modulo dai pacchetti. Nessuna dipendenza, più istanze per pagina.
   Con "riduci movimento" niente avanzamento automatico né animazioni. */
(() => {
  if (window.__vbxDigital) return;
  window.__vbxDigital = true;

  const reduceMq = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reduced = () => reduceMq.matches;
  const designMode = () => Boolean(window.Shopify && window.Shopify.designMode);

  /* ---------- comparse allo scroll ----------
     IntersectionObserver + un controllo leggero allo scroll: ciò che è già sopra
     il bordo inferiore dello schermo viene sempre mostrato, anche con scroll velocissimi. */
  let revealIO = null;
  const pending = new Set();
  let ticking = false;
  const reveal = (el) => {
    el.classList.add('is-in');
    pending.delete(el);
    if (revealIO) revealIO.unobserve(el);
  };
  const sweep = () => {
    ticking = false;
    const limit = window.innerHeight * 0.96;
    pending.forEach((el) => { if (el.getBoundingClientRect().top < limit) reveal(el); });
  };
  const onScroll = () => {
    if (ticking || !pending.size) return;
    ticking = true;
    window.requestAnimationFrame(sweep);
  };
  const scanReveal = (root) => {
    const els = (root || document).querySelectorAll('[data-dg-reveal]:not(.is-in)');
    if (!els.length) return;
    if (!('IntersectionObserver' in window) || reduced() || designMode()) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    document.documentElement.classList.add('vbx-dg-anim');
    if (!revealIO) {
      revealIO = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting) reveal(entry.target); });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0 });
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
    }
    els.forEach((el) => { pending.add(el); revealIO.observe(el); });
    onScroll();
  };

  /* ---------- hero: il diagramma racconta un movimento alla volta ---------- */
  class VbxDgViz extends HTMLElement {
    connectedCallback() {
      this.ev = 0;
      this.svgs = Array.from(this.querySelectorAll('svg.vbx-dg-viz__links'));
      if (reduced() || !('IntersectionObserver' in window)) return;
      this.io = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) this.start();
        else this.stop();
      });
      this.io.observe(this);
    }

    disconnectedCallback() {
      this.stop();
      if (this.io) this.io.disconnect();
    }

    start() {
      if (this.timer) return;
      this.svgs.forEach((svg) => svg.unpauseAnimations && svg.unpauseAnimations());
      const tick = () => {
        this.ev = (this.ev % 4) + 1;
        this.dataset.ev = String(this.ev);
      };
      tick();
      this.timer = window.setInterval(tick, 2600);
    }

    stop() {
      window.clearInterval(this.timer);
      this.timer = null;
      this.svgs.forEach((svg) => svg.pauseAnimations && svg.pauseAnimations());
    }
  }

  /* ---------- simulatore "Guarda come si sincronizza" ---------- */
  class VbxDgSync extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('.vbx-dg-stage');
      this.items = Array.from(this.querySelectorAll('.vbx-dg-step'));
      this.buttons = this.items.map((li) => li.querySelector('.vbx-dg-step__btn'));
      if (!this.stage || !this.items.length) return;

      this.index = 0;
      this.timers = [];
      this.interval = Number(this.dataset.interval) || 7000;
      this.style.setProperty('--dur', this.interval + 'ms');
      this.delays = (this.stage.dataset.delays || '').split(',').map((n) => Number(n) || 1000);
      this.stopped = reduced(); // fermo per scelta dell'utente o per "riduci movimento"
      this.hover = false;
      this.visible = false;
      this.started = false;
      this.toggleBtn = this.querySelector('[data-dg-toggle]');

      this.buttons.forEach((btn, i) => {
        btn.addEventListener('click', () => {
          this.stopped = true; // scelta manuale: niente più avanzamento automatico
          this.started = true;
          this.go(i);
          this.sync();
        });
        btn.addEventListener('keydown', (e) => this.onKey(e, i));
      });

      if (this.toggleBtn && !reduced()) {
        this.toggleBtn.hidden = false;
        this.toggleBtn.addEventListener('click', () => {
          this.stopped = !this.stopped;
          this.sync();
          if (!this.stopped) this.runBar();
        });
      }

      this.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        this.hover = true;
        this.sync();
      });
      this.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        this.hover = false;
        this.sync();
      });
      // chi arriva da tastiera prende il controllo: l'avanzamento si ferma (riparte solo col pulsante)
      this.addEventListener('focusin', (e) => {
        if (e.target === this.toggleBtn) return;
        let keyboard = true;
        try { keyboard = e.target.matches(':focus-visible'); } catch (err) { keyboard = true; }
        if (keyboard && !this.stopped) {
          this.stopped = true;
          this.sync();
        }
      });
      this.addEventListener('animationend', (e) => {
        if (e.animationName !== 'vbx-dg-progress') return;
        if (e.target.closest('.vbx-dg-step') !== this.items[this.index]) return;
        if (this.stopped) return;
        this.go((this.index + 1) % this.items.length);
      });

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          if (this.visible && !this.started) {
            this.started = true;
            this.go(this.index);
          }
          this.sync();
        }, { threshold: 0.3 });
        this.io.observe(this.stage);
      } else {
        this.started = true;
        this.visible = true;
        this.go(0);
      }
      this.sync();
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      this.clearTimers();
    }

    onKey(e, i) {
      const last = this.buttons.length - 1;
      const map = { ArrowDown: i + 1, ArrowRight: i + 1, ArrowUp: i - 1, ArrowLeft: i - 1, Home: 0, End: last };
      if (!(e.key in map)) return;
      e.preventDefault();
      let next = map[e.key];
      if (next > last) next = 0;
      if (next < 0) next = last;
      this.buttons[next].focus();
    }

    go(i) {
      this.index = i;
      this.items.forEach((li, k) => {
        const on = k === i;
        li.classList.toggle('is-active', on);
        if (this.buttons[k]) this.buttons[k].setAttribute('aria-expanded', on ? 'true' : 'false');
        const bar = li.querySelector('.vbx-dg-step__bar i');
        if (bar) bar.classList.remove('is-run');
      });
      this.stage.dataset.step = String(i + 1);
      this.runBar();
      this.play(i);
    }

    runBar() {
      const bar = this.items[this.index] && this.items[this.index].querySelector('.vbx-dg-step__bar i');
      if (!bar || this.stopped || reduced()) return;
      bar.classList.remove('is-run');
      void bar.offsetWidth;
      bar.classList.add('is-run');
    }

    sync() {
      const playing = this.started && this.visible && !this.stopped && !this.hover && !reduced();
      this.classList.toggle('is-playing', playing);
      this.classList.toggle('is-stopped', this.stopped);
      if (this.stopped) {
        this.items.forEach((li) => {
          const bar = li.querySelector('.vbx-dg-step__bar i');
          if (bar) bar.classList.remove('is-run');
        });
      }
    }

    clearTimers() {
      this.timers.forEach((t) => window.clearTimeout(t));
      this.timers = [];
      if (this.raf) window.cancelAnimationFrame(this.raf);
    }

    later(fn, ms) {
      this.timers.push(window.setTimeout(fn, ms));
    }

    /* anima il passo i: classi CSS (rilanciate) + numeri che cambiano ovunque insieme */
    play(i) {
      this.clearTimers();
      const animate = !reduced();
      if (animate) {
        this.stage.classList.remove('is-anim');
        void this.stage.offsetWidth;
        this.stage.classList.add('is-anim');
      }
      const delay = this.delays[i] || 1000;
      const counters = [];
      this.stage.querySelectorAll('[data-dg-seq]').forEach((el) => {
        const seq = el.dataset.dgSeq.split('|');
        const from = seq[i];
        const to = seq[i + 1];
        if (from === undefined || to === undefined) return;
        el.classList.remove('is-flash');
        if (!animate) {
          el.textContent = to;
          return;
        }
        el.textContent = from;
        if (from !== to) counters.push({ el, from, to });
      });
      if (!animate || !counters.length) return;
      this.later(() => {
        counters.forEach(({ el, from, to }) => this.count(el, from, to));
        const inv = this.stage.querySelector('.vbx-dg-inv');
        if (inv) {
          inv.classList.add('is-pulse');
          this.later(() => inv.classList.remove('is-pulse'), 1100);
        }
      }, delay);
    }

    count(el, from, to) {
      el.classList.add('is-flash');
      this.later(() => el.classList.remove('is-flash'), 1500);
      const a = parseInt(from, 10);
      const b = parseInt(to, 10);
      if (Number.isNaN(a) || Number.isNaN(b) || /^\+/.test(from) || /^\+/.test(to)) {
        el.textContent = to;
        return;
      }
      const t0 = performance.now();
      const dur = Math.min(900, 260 + Math.abs(b - a) * 30);
      const frame = (now) => {
        const p = Math.min(1, (now - t0) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = String(Math.round(a + (b - a) * eased));
        if (p < 1) this.raf = window.requestAnimationFrame(frame);
      };
      this.raf = window.requestAnimationFrame(frame);
    }
  }

  if (!customElements.get('vbx-dg-viz')) customElements.define('vbx-dg-viz', VbxDgViz);
  if (!customElements.get('vbx-dg-sync')) customElements.define('vbx-dg-sync', VbxDgSync);

  /* ---------- pacchetti: preseleziona "Cosa ti interessa" nel modulo ---------- */
  document.addEventListener('click', (e) => {
    const link = e.target.closest && e.target.closest('a[data-dg-interest]');
    if (!link) return;
    const select = document.querySelector('select[data-dg-interest-select]');
    if (!select) return;
    const wanted = (link.dataset.dgInterest || '').trim().toLowerCase();
    const option = Array.from(select.options).find((o) => o.value.trim().toLowerCase() === wanted);
    if (!option) return;
    select.value = option.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- avvio + editor del tema ---------- */
  const init = () => scanReveal(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', (e) => scanReveal(e.target));
})();
